import {
  Box,
  Grid,
  GridItem,
  FormControl,
  FormLabel,
  Flex,
  Heading,
  Text,
  Button,
  Input,
  Select,
  VStack,
  Image,
  HStack,
  useColorModeValue,
  useBreakpointValue,
  Container,
  Stack,
  Circle,
  SimpleGrid,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Link,
} from "@chakra-ui/react";
import { MdOutlineArrowForward } from "react-icons/md";
import { FaSearch } from "react-icons/fa";
import { FaShieldAlt, FaExchangeAlt, FaUsers, FaGlobe, FaPhoneAlt, FaEnvelope } from "react-icons/fa";
import Header from "../../components/Header";
import { motion } from "framer-motion";
import { NavLink, Link as RouterLink } from "react-router-dom";
import { PeerxLogo } from "../../components/icons/Icons";
import PricingPlans from "../../components/Billing/PricingPlans";

import { useTranslation } from "react-i18next";

// Feature Icons and Descriptions
const FEATURES = [
  { key: "trusted_by_thousands", icon: FaUsers },
  { key: "secure_connections", icon: FaShieldAlt },
  { key: "global_peer_network", icon: FaGlobe },
  { key: "real_time_connections", icon: FaExchangeAlt },
];

// FAQ content
const FAQS = [
  { key: "faq_security" },
  { key: "faq_dispute" },
  { key: "faq_trial" },
];

function HomePage() {
  const { t, i18n } = useTranslation();

  const isSmallScreen = useBreakpointValue({ base: true, md: false });
  const isMobile = useBreakpointValue({ base: true, md: false });


  const textColor = useColorModeValue("brand.400", "white");
  const bgColor = useColorModeValue("green.50", "gray.900");
  const formBgColor = useColorModeValue("white", "gray.800");
  const accentColor = useColorModeValue("brand.400", "green.300");
  const cardBgColor = useColorModeValue("white", "gray.700");

    // Handle Language Change
    const handleLanguageChange = (event) => {
      i18n.changeLanguage(event.target.value);
    };

  return (
    <Box bg={bgColor} color={textColor} fontFamily="Poppins, sans-serif" minH="100vh" overflow="hidden">

      {/* Header Section */}
      <Container maxW="container.xl" py={6}>
        <Header />

        {/* Hero Section */}
        <Box position="relative" py={{ base: 8, md: 12 }}>

          <Flex bg="brand.200" pb={{ base: 0, md: 12 }} pt={{ base: 25, md: 12 }} px={{ base: 4, md: 20 }} borderRadius="3xl" direction={{ base: "column", md: "row" }} align="center" justify="space-between" zIndex={1} position="relative">
            {/* Left Content */}
            <VStack align="start" spacing={6} >
              <Heading size={{ base: "2xl", md: "3xl" }} maxW={{ base: "100%", md: "600px" }} fontWeight="bold" color={accentColor} lineHeight="short">
              {t("homepage.hero_title")}
              </Heading>
              <Text fontSize={{ base: "2xl", md: "2xl" }} maxW={{ base: "100%", md: "600px" }} color="gray.600">
              {t("homepage.hero_subtitle")}
              </Text>
              <Text mt="30px" fontSize="lg" lineHeight="tall" maxW={{ base: "100%", md: "600px" }}>
              {t("homepage.hero_description")}
              </Text>
              <Button mt="29px" bg="white" color="brand.400" colorScheme="white" size="lg" borderRadius="full" shadow="lg" _hover={{ transform: "scale(1.05)", shadow: "xl" }} as={RouterLink} to="/personal/listings">
              {t("homepage.explore_listings")}
              </Button>
              {/* <HStack spacing={4}>
                <Image src="/path/to/user1.jpg" alt="User 1" boxSize="40px" borderRadius="full" />
                <Image src="/path/to/user2.jpg" alt="User 2" boxSize="40px" borderRadius="full" />
                <Text fontSize="sm" color="gray.500">John and 30+ joined today</Text>
              </HStack> */}
            </VStack>

            {/* Right Image */}
            <Box position="relative" flex="1" zIndex={2} mt={{ base: 3, md: 0 }}>
              <Image
                src="/peerx-member.png"
                alt={t("homepage.peerx_member_image_alt")}
                position={{ base: "relative", md: "absolute" }}
                top={{ md: "-60%" }}
                right={{ md: "-35%" }}
                // width="500px"
                maxW={{ base: "500px", md: "600px" }}
                zIndex={1}
                marginTop={{ base: "0px", md: "-350px" }}
              />
            </Box>
          </Flex>
        </Box>
      </Container>

      {/* Features Section */}
      <Box py={16} px={8} id="features" bg="brand.200">
        <Container maxW="container.xl">
          <Heading textAlign="center" mb={10} color={textColor} size="4xl" >
          {t("homepage.why_choose_peerx")}
          </Heading>
          <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={8}>
            {FEATURES.map((feature, index) => (
              <VStack
                key={index}
                bg={cardBgColor}
                p={8}
                borderRadius="md"
                shadow="lg"
                _hover={{ transform: "scale(1.05)", shadow: "xl" }}
              >
                <Circle size="60px" bg={accentColor} color="white">
                  <feature.icon size="30px" />
                </Circle>
                <Heading size="md" mt={4}>
                {t(`homepage.${feature.key}_title`)}
                </Heading>
                <Text textAlign="center" mt={2}>
                {t(`homepage.${feature.key}_description`)}
                </Text>
              </VStack>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      {/* Pricing Section */}
      <Box pt={16} id="pricing" bg="gray.100">
        <Container maxW="container.xl">
          <Heading textAlign="center" mb={10} color="brand.400" fontSize="4xl">
          {t("homepage.pricing_plans")}
          </Heading>
          <PricingPlans />
        </Container>
      </Box>

      {/* FAQ Section */}
      <Box py={16} px={8} id="faq" bg={useColorModeValue("gray.100", "gray.800")}>
        <Container maxW="container.md">
          <Heading textAlign="center" mb={10} color={textColor} fontSize="4xl">
          {t("homepage.faq_title")}
          </Heading>
          <Accordion allowToggle>
            {FAQS.map((faq, index) => (
              <AccordionItem key={index}>
                <AccordionButton _expanded={{ bg: accentColor, color: "white" }}>
                  <Box flex="1" textAlign="left">
                  {t(`homepage.${faq.key}_question`)}
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
                <AccordionPanel pb={4}>
                {t(`homepage.${faq.key}_answer`)}
                </AccordionPanel>
              </AccordionItem>
            ))}
          </Accordion>
        </Container>
      </Box>

      {/* Call-to-Action Section */}
      <Flex
        direction="column"
        align="center"
        justify="center"
        py={16}
        bg="brand.400"
        color="white"
        textAlign="center"
      >
        <Heading textAlign="center" mb={10} color="whiteAlpha.900" size="4xl" >          
          {t("homepage.cta_title")}
        </Heading>
        <Text mb={6}>{t("homepage.cta_subtitle")}</Text>
        <Button
          as={RouterLink}
          to="/auth/sign-up"
          size="lg"
          bg="white"
          color="brand.400"
          _hover={{ bg: "brand.300", transform: "scale(1.1)", shadow: "xl" }}
        >
          {t("homepage.cta_button")}
          </Button>
      </Flex>

      {/* Contact Information Section */}
      {/* <Box py={16} bg="brand.100" color={textColor}>
        <Container maxW="container.lg">
          <Heading textAlign="center" mb={10} color="brand.400" size="2xl">
            Contact Us
          </Heading>
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8} textAlign="center">
            <VStack>
              <Circle size="60px" bg={accentColor} color="white">
                <FaPhoneAlt size="30px" />
              </Circle>
              <Text fontWeight="bold" mt={4}>Phone Support</Text>
              <Text>+1 (438) 807-2347</Text>
            </VStack>

            <VStack>
              <Circle size="60px" bg={accentColor} color="white">
                <FaEnvelope size="30px" />
              </Circle>
              <Text fontWeight="bold" mt={4}>Email Support</Text>
              <Text>support@peerx.com</Text>
            </VStack>

            <VStack>
              <Circle size="60px" bg={accentColor} color="white">
                <MdOutlineArrowForward size="30px" />
              </Circle>
              <Text fontWeight="bold" mt={4}>Hours of Operation</Text>
              <Text>Mon-Fri: 9am - 6pm (EST)</Text>
            </VStack>
          </SimpleGrid>
        </Container>
      </Box> */}

      {/* Footer Section */}
      <Box bg="brand.200" py="40px">
        <Container maxW="container.xl">
          <Flex
            direction={{ base: "column", md: "row" }}
            justify={{ base: "center", md: "space-around" }}
            align="center"
            wrap="wrap"
            color="white"
            gap={4}  // Smaller, consistent gap for mobile
            px={4}   // Added padding for spacing on smaller screens
          >
            <Text textAlign="center" mb={{ base: 4, md: 0 }}>
            &copy; 2024 PeerX. {t("homepage.footer_rights")}
            </Text>
            <Link
              href="/privacy-policy"
              _hover={{ textDecoration: "underline" }}
              color="white"
              textAlign="center"
            >
              {t("homepage.footer_privacy")}
              </Link>
            <Link
              href="/terms-of-use"
              _hover={{ textDecoration: "underline" }}
              color="white"
              textAlign="center"
            >
              {t("homepage.footer_terms")}
              </Link>
          </Flex>
        </Container>
      </Box>

{/* Language Switcher */}
      <Box position="fixed" bottom="7" right="4" zIndex="120" p={2}>
        <Select
          value={i18n.language}
          onChange={handleLanguageChange}
          width="120px"
          bg="white"
          color="black"
          size="sm"
        >
          <option value="en">English</option>
          <option value="fr">Français</option>
          <option value="es">Español</option>
        </Select>
      </Box>

    </Box>
  );
}

export default HomePage;